import React from 'react';
import { MainLayout } from '../../components';
import { Box, Tabs, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { paymentsTabRoute } from '@uplift/constants';
import { AddressVerificationProvider, Loader, StickyStackItem, Tab } from '@uplift/ui-v2';
import { useHistory } from 'react-router';
import { OutstandingPaymentsTable } from './components/PaymentsOverviewTable/OutstandingPaymentsTable.component';
import { ProcessingPaymentsTable } from './components/PaymentsOverviewTable/ProcessingPaymentsTable.component';
import { CompletedPaymentsTable } from './components/PaymentsOverviewTable/CompletedPaymentsTable.component';
import { BillingPaymentButton, BillingSettingsCardsSection, BillingSettingsPaymentMethodSection } from './components/billing';
import { BillingContextProvider } from './components/billing/billing.context';
import { useBetterhelpCoverageQuery, usePatientServiceItemsQuery } from '@uplift/queries';
import { useCurrentuser } from '@uplift/contexts';
import { PatientServiceItemStatus } from '@uplift/api';
import { NoSessionsHistory } from './components/NoSessionsHistory.component';
import { mobileBreakpoint, useIsMobile } from '@uplift/hooks';
import { CoverageTab } from './components/CoverageTab/CoverageTab.component';


const useStyles = makeStyles()((theme) => ({
  container: {
    maxWidth: '100%',
    boxSizing: 'content-box',
    [theme.breakpoints.up('sm')]: {
      maxWidth: '769px',
      padding: theme.spacing(3),
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: '769px',
      padding: theme.spacing(5, 10),
    }
  },
  tabsContainer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
    zIndex: theme.zIndex.appBar - 1,
  },
  tabs: {
    boxShadow: `inset 0 -1px 0 ${theme.palette.divider}`,
  },
}));


const PaymentsContent = () => {
  const me = useCurrentuser();
  const { data } = usePatientServiceItemsQuery(me.id, { suspense: true });
  const isMobile = useIsMobile(mobileBreakpoint);
  const mobilePadding = isMobile ? 8 : 0;


  if (!data.completed && !data.outstanding && !data.processing)
    return (
      <NoSessionsHistory />
    );

  return (
    <Box display="flex" flexDirection="column" gap="40px" pb={mobilePadding}>
      <OutstandingPaymentsTable items={data[PatientServiceItemStatus.outstanding] ?? []} />
      <ProcessingPaymentsTable items={data[PatientServiceItemStatus.processing] ?? []} />
      <CompletedPaymentsTable items={data[PatientServiceItemStatus.completed] ?? []} />
    </Box>
  );

};


export const PaymentsPage: React.VFC = () => {
  const history = useHistory();
  const { tab = 'overview' } = paymentsTabRoute.useParams();
  const { classes } = useStyles();
  const isMobile = useIsMobile(mobileBreakpoint);
  const me = useCurrentuser();
  const { data: coverage = [] } = useBetterhelpCoverageQuery(me.id);
  const hasCoverage = coverage.length > 0;

  return (
    <MainLayout noContainerPadding noTopPadding>
      <AddressVerificationProvider shouldReverifyAddress={false} parsedVerifiedAddress={null}>
        <BillingContextProvider>
          <Box className={classes.container}>
            <Box boxSizing="border-box">
              {!isMobile && (
                <Box mb={4}>
                  <Typography variant="h3">Payments</Typography>
                </Box>
              )}
              <BillingSettingsCardsSection />
              {!isMobile && <Box pb={5} />}
            </Box>

            <StickyStackItem placement="top" order={0}>
              <Box className={classes.tabsContainer}>
                <Tabs
                  className={classes.tabs}
                  value={tab}
                  onChange={(_, tab) => history.push(paymentsTabRoute.createLink({ tab }))}
                  variant={isMobile ? 'fullWidth' : 'standard'}
                  indicatorColor="secondary"
                  textColor="secondary"
                >
                  <Tab label="Payments" value="overview" style={{ minWidth: 160 }} />
                  <Tab label="Payment method" value="method" />
                  {hasCoverage && <Tab label="Coverage" value="coverage" />}
                </Tabs>
              </Box>
            </StickyStackItem>

            <React.Suspense fallback={<Loader height="300px" />}>
              <Box>
                {tab === 'overview' && (
                  <Box pt={5} bgcolor={{ xs: 'grey.50', sm: 'white' }}>
                    <PaymentsContent />
                    <BillingPaymentButton />

                  </Box>
                )}
                {tab === 'method' && (
                  <Box mb={16}>
                    <BillingSettingsPaymentMethodSection />
                  </Box>
                )}
                {tab === 'coverage' && hasCoverage && (
                  <Box mb={16} pt={5}>
                    <CoverageTab referral={coverage[0]} />
                  </Box>
                )}
              </Box>
            </React.Suspense>
          </Box>
        </BillingContextProvider>
      </AddressVerificationProvider>
    </MainLayout>
  );
};
